import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
} from '@mui/material';
import GenericDialog from '../GenericDialog';
import { DigitalSection } from '../../redux/types';
import DigitalSectionOrganizer from './DigitalSectionOrganizer';

type Props = {
  digitalSections?: DigitalSection[];
  selectedSection?: DigitalSection;
  submissionExist?: boolean;
  setSelectedSection: (sectionId?: number) => void;
  theme?: 'tertiary';
  labelText?: string;
  showMultipleSectionSelectionCheckBox?: boolean;
}

const DigitalSectionToolbar: React.FC<Props> = ({
  digitalSections,
  selectedSection,
  submissionExist,
  setSelectedSection,
  labelText = 'Antibody',
  showMultipleSectionSelectionCheckBox,
  theme,
}: Props) => {
  /* updateIndeicesRef holds the callback reference from DigitalSectionOrganizer
   * that will be triggered on Save handler. Ref is used instead of state because
   * no UI updates are required based on this value */
  const updateIndicesRef = useRef<() => void>();
  const [showSettings, setShowSettings] = useState<boolean>(false);

  useEffect(() => {
    if (digitalSections?.length && selectedSection === undefined) {
      setSelectedSection(digitalSections[0].id);
    }
  }, [
    digitalSections,
    selectedSection,
    setSelectedSection,
  ]);

  const onSaveSectionOrder = () => {
    if (updateIndicesRef.current) {
      updateIndicesRef.current();
      updateIndicesRef.current = undefined;
    }
  };

  const onReorder = useCallback((updateIndices: () => void) => {
    updateIndicesRef.current = updateIndices;
  }, []);

  const onSectionChange = (
    event: SelectChangeEvent<number>,
  ) => {
    const secId = event.target.value;
    setSelectedSection(secId as number);
  };

  return (
    <>
      <Box display="flex" gap={1} alignItems="center">
        <InputLabel id="digital-section-selector">{labelText}</InputLabel>
        {
            selectedSection && (
              <Select
                size="small"
                labelId="digital-section-selector"
                id="digital-section-selector"
                value={selectedSection?.id}
                label={labelText}
                onChange={onSectionChange}
              >
                {
                  digitalSections?.map((ds) => (
                    <MenuItem key={ds.id} value={ds.id}>
                      <em>{ds.name}</em>
                    </MenuItem>
                  ))
                }
              </Select>
            )
          }
        <Button variant="outlined" color={theme ? 'tertiary' : undefined} size="large" onClick={() => setShowSettings(true)}><FormattedMessage id="createeditsurveyview.settings" /></Button>
      </Box>
      <GenericDialog
        maxWidth="sm"
        isOpen={showSettings}
        onRequestClose={() => setShowSettings(false)}
      >
        <DigitalSectionOrganizer
          submissionExist={submissionExist}
          onReorder={onReorder}
          labelText={labelText}
          showMultipleSectionSelectionCheckBox={showMultipleSectionSelectionCheckBox}
          onSaveSectionOrder={onSaveSectionOrder}
        />
      </GenericDialog>
    </>
  );
};

export default DigitalSectionToolbar;
